import { useState } from 'react';
import { authClient, signOutSession, adminSetupRetirement } from '../../lib/auth';
import { customerAccountSetup } from '../../lib/oem-client';
import { pinRequest, validStaffPin } from '../../lib/staff-pin';
import { sealCachedIdentity } from '../../lib/oem-offline';
import { hasPinSession, clearPinSession } from '../../lib/pin-session';
import { roleLabel } from './display-copy';

import { adminSetupCapability } from '../../lib/admin-setup';
export { adminSetupCapability } from '../../lib/admin-setup';

export function AdminAccountSetup() {
  const [name, setName] = useState(''),
    [pin, setPin] = useState(''),
    [busy, setBusy] = useState(false),
    [notice, setNotice] = useState('');
  return (
    <main className="frame login-frame">
      <section className="card">
        <div className="brand">
          <span className="wordmark">SQI</span>
        </div>
        <h1>ตั้งรหัสส่วนตัวผู้ดูแล</h1>
        <p>ชื่อที่เลือกจะแสดงในหน้าเข้าสู่ระบบทีมงาน ใช้สิทธิ์ผู้ดูแลเดิมของคุณ</p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setBusy(true);
            void adminSetupRetirement
              .then((retired) => {
                if (!retired) throw Error('previous_identity_unavailable');
                return pinRequest('/admin-setup', { capability: adminSetupCapability, name, pin });
              })
              .then(
                () => {
                  setPin('');
                  setNotice('บันทึกแล้ว เลือกชื่อและเข้าสู่ระบบด้วยรหัสส่วนตัว');
                },
                () => {
                  setPin('');
                  setNotice('ตั้งรหัสไม่ได้ ลิงก์อาจหมดอายุหรือถูกใช้แล้ว กรุณาติดต่อผู้ดูแลระบบ');
                },
              )
              .finally(() => setBusy(false));
          }}
        >
          <label>
            ชื่อที่แสดง
            <input
              value={name}
              maxLength={100}
              required
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <label>
            รหัสส่วนตัว 6 หลัก
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              autoComplete="new-password"
              required
              value={pin}
              onChange={(e) => setPin(e.target.value)}
            />
          </label>
          <button className="action" disabled={busy || !navigator.onLine || !validStaffPin(pin)}>
            บันทึกรหัสส่วนตัว
          </button>
        </form>
        {notice ? <p role="status">{notice}</p> : null}
        <a href="/login">กลับหน้าเข้าสู่ระบบ</a>
      </section>
    </main>
  );
}
export function CustomerUsernameLogin() {
  const [username, setUsername] = useState(''),
    [password, setPassword] = useState(''),
    [confirmation, setConfirmation] = useState(''),
    [busy, setBusy] = useState(false),
    [notice, setNotice] = useState(''),
    [consent, setConsent] = useState(false);
  const [operation, setOperation] = useState<string | null>(null),
    [scope, setScope] = useState<Awaited<ReturnType<typeof customerAccountSetup>> | null>(null);
  async function login() {
    if (!authClient || !navigator.onLine) return;
    setBusy(true);
    setNotice('');
    try {
      const result = (await pinRequest('/customer-login', { username, password })) as {
        accessToken: string;
        refreshToken: string;
        operationId: string | null;
      };
      if (hasPinSession()) await signOutSession();
      else {
        const previous = await authClient.auth.getSession();
        if (previous.data.session) {
          await sealCachedIdentity(previous.data.session.user.id);
          const retired = await authClient.auth.signOut({ scope: 'local' });
          if (retired.error) throw retired.error;
        }
      }
      const installed = await authClient.auth.setSession({
        access_token: result.accessToken,
        refresh_token: result.refreshToken,
      });
      if (installed.error) throw installed.error;
      clearPinSession();
      setPassword('');
      if (result.operationId) {
        setOperation(result.operationId);
        setScope(await customerAccountSetup(result.operationId));
      } else {
        if (window.location.pathname === '/login') history.replaceState(null, '', '/portal');
        dispatchEvent(new Event('sqi-session-changed'));
      }
    } catch {
      setPassword('');
      setNotice('เข้าสู่ระบบไม่ได้ กรุณาตรวจสอบชื่อผู้ใช้และรหัสผ่าน หรือติดต่อผู้ดูแล');
    } finally {
      setBusy(false);
    }
  }
  async function accept() {
    if (!operation || !consent || password.length < 12 || password !== confirmation) return;
    setBusy(true);
    setNotice('');
    try {
      await customerAccountSetup(operation, true, password);
      await signOutSession();
      setPassword('');
      setConfirmation('');
      setOperation(null);
      setScope(null);
      setConsent(false);
      setNotice('ตั้งรหัสผ่านส่วนตัวและรับคำเชิญแล้ว เข้าสู่ระบบด้วยรหัสผ่านใหม่');
    } catch {
      setNotice(
        'ตั้งรหัสผ่านไม่ได้ ต้องต่างจากรหัสเริ่มต้น หากบันทึกแล้วให้ลองรหัสส่วนตัวเดิมอีกครั้ง',
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void (operation ? accept() : login());
      }}
    >
      {operation ? (
        <>
          <h2>ตั้งรหัสผ่านส่วนตัว</h2>
          <p>เลือกรหัสผ่านใหม่ที่ต่างจากรหัสเริ่มต้น ก่อนรับสิทธิ์ในโครงการ</p>
        </>
      ) : (
        <label>
          ชื่อผู้ใช้
          <input
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            value={username}
            required
            maxLength={40}
            onChange={(e) => setUsername(e.target.value)}
          />
        </label>
      )}
      <label>
        {operation ? 'รหัสผ่านส่วนตัวใหม่' : 'รหัสผ่าน'}
        <input
          type="password"
          autoComplete={operation ? 'new-password' : 'current-password'}
          minLength={12}
          maxLength={1024}
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </label>
      {operation ? (
        <>
          <label>
            ยืนยันรหัสผ่าน
            <input
              type="password"
              autoComplete="new-password"
              minLength={12}
              required
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
            />
          </label>
          {scope ? (
            <label>
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                required
              />
              รับคำเชิญเข้าถึง {scope.label} · {roleLabel(scope.role)} · ถึง{' '}
              {new Date(scope.expiresAt).toLocaleString('th-TH')}
            </label>
          ) : (
            <p role="status">กำลังตรวจสอบคำเชิญ</p>
          )}
        </>
      ) : null}
      <button
        className="action"
        disabled={
          busy ||
          !navigator.onLine ||
          Boolean(operation && (!scope || !consent || password !== confirmation))
        }
      >
        {operation ? 'ตั้งรหัสผ่านและรับคำเชิญ' : 'เข้าสู่ระบบลูกค้า'}
      </button>
      {operation ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => void signOutSession().then(() => window.location.replace('/login'))}
        >
          ยกเลิกและออกจากระบบ
        </button>
      ) : (
        <p>บัญชีสร้างโดยทีมงาน หากยังไม่มีชื่อผู้ใช้หรือลืมรหัสผ่าน กรุณาติดต่อผู้ดูแล</p>
      )}
      {notice ? <p role="status">{notice}</p> : null}
    </form>
  );
}
